import { z } from 'zod';
import { cursorPageSchema, cursorQuerySchema, isoDateTimeSchema, localeSchema, uuidSchema, } from './common.js';
import { abuseReportStatusSchema, aiProviderSchema, domainRuleModeSchema, importStatusSchema, jobStatusSchema, userRoleSchema, userStatusSchema, } from './enums.js';
export const adminUserSchema = z.object({
    id: uuidSchema,
    email: z.string(),
    displayName: z.string(),
    role: userRoleSchema,
    status: userStatusSchema,
    locale: localeSchema,
    emailVerified: z.boolean(),
    recipeCount: z.number().int(),
    storageBytes: z.number().int(),
    /** AI features are unlocked for this user (by an administrator). */
    aiEnabled: z.boolean(),
    /** The user's own consent; only possible while `aiEnabled`. */
    aiConsentAt: isoDateTimeSchema.nullable(),
    lastLoginAt: isoDateTimeSchema.nullable(),
    createdAt: isoDateTimeSchema,
});
export const adminUserListQuerySchema = cursorQuerySchema.extend({
    q: z.string().max(200).optional(),
    status: userStatusSchema.optional(),
    role: userRoleSchema.optional(),
});
export const adminUserPageSchema = cursorPageSchema(adminUserSchema);
export const adminUpdateUserRequestSchema = z.object({
    status: userStatusSchema.optional(),
    role: userRoleSchema.optional(),
    aiEnabled: z.boolean().optional(),
    reason: z.string().max(500).optional(),
});
export const systemHealthSchema = z.object({
    status: z.enum(['ok', 'degraded', 'down']),
    version: z.string(),
    uptimeSeconds: z.number(),
    checks: z.array(z.object({
        name: z.string(),
        status: z.enum(['up', 'down']),
        latencyMs: z.number().nullable(),
        detail: z.string().nullable(),
    })),
    storage: z.object({
        totalAssets: z.number().int(),
        totalBytes: z.number().int(),
    }),
    queues: z.array(z.object({
        name: z.string(),
        waiting: z.number().int(),
        active: z.number().int(),
        completed: z.number().int(),
        failed: z.number().int(),
        delayed: z.number().int(),
        paused: z.boolean(),
    })),
});
export const adminJobSchema = z.object({
    id: z.string(),
    queue: z.string(),
    name: z.string(),
    status: jobStatusSchema,
    attemptsMade: z.number().int(),
    failedReason: z.string().nullable(),
    progress: z.number().nullable(),
    createdAt: isoDateTimeSchema.nullable(),
    processedAt: isoDateTimeSchema.nullable(),
    finishedAt: isoDateTimeSchema.nullable(),
    /** Payload with sensitive fields redacted. */
    data: z.record(z.unknown()),
});
export const adminJobListQuerySchema = z.object({
    queue: z.string().optional(),
    status: jobStatusSchema.optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
export const adminJobActionSchema = z.object({
    action: z.enum(['retry', 'remove', 'promote']),
});
export const featureFlagSchema = z.object({
    key: z.string(),
    enabled: z.boolean(),
    description: z.string().nullable(),
    config: z.record(z.unknown()).nullable(),
    updatedAt: isoDateTimeSchema.nullable(),
});
export const FEATURE_FLAG_KEYS = [
    'ai',
    'source_search',
    'speech',
    'public_sharing',
    'image_import',
    'headless_scraper',
    'tasting_notes',
    'ingredient_catalog',
    'barcode_scan',
    'recipe_nutrition',
    'shopping_live',
    'support_inbox',
    'prices',
    'price_tag_scan',
    'receipt_scan',
    'custom_backgrounds',
    'ai_chat',
];
export const featureFlagKeySchema = z.enum(FEATURE_FLAG_KEYS);
export const featureFlagUpdateSchema = z.object({
    enabled: z.boolean().optional(),
    config: z.record(z.unknown()).nullable().optional(),
});
export const aiProviderConfigSchema = z.object({
    provider: aiProviderSchema,
    enabled: z.boolean(),
    hasApiKey: z.boolean(),
    defaultModel: z.string(),
    visionModel: z.string(),
    priority: z.number().int(),
    limits: z.object({
        maxRequestsPerUserPerDay: z.number().int(),
        maxOutputTokens: z.number().int(),
        maxCostUsdPerUserPerDay: z.number().nullable(),
    }),
    region: z.string().nullable(),
});
export const aiProviderConfigUpdateSchema = aiProviderConfigSchema
    .omit({ provider: true, hasApiKey: true })
    .partial();
export const scraperFailureSchema = z.object({
    id: uuidSchema,
    domain: z.string(),
    url: z.string(),
    status: importStatusSchema,
    errorCode: z.string().nullable(),
    errorMessage: z.string().nullable(),
    strategy: z.string().nullable(),
    createdAt: isoDateTimeSchema,
});
export const scraperStatsSchema = z.object({
    byStrategy: z.array(z.object({ strategy: z.string(), success: z.number().int(), failed: z.number().int() })),
    byDomain: z.array(z.object({ domain: z.string(), success: z.number().int(), failed: z.number().int() })),
});
export const domainRuleSchema = z.object({
    id: uuidSchema,
    domain: z.string(),
    mode: domainRuleModeSchema,
    note: z.string().nullable(),
    createdAt: isoDateTimeSchema,
});
export const domainRuleWriteSchema = z.object({
    domain: z.string().trim().min(1).max(253).toLowerCase(),
    mode: domainRuleModeSchema,
    note: z.string().max(300).nullable().optional(),
});
export const abuseReportSchema = z.object({
    id: uuidSchema,
    reporterId: uuidSchema,
    recipeId: uuidSchema,
    recipeTitle: z.string().nullable(),
    reason: z.string(),
    status: abuseReportStatusSchema,
    createdAt: isoDateTimeSchema,
    resolvedAt: isoDateTimeSchema.nullable(),
});
export const abuseReportWriteSchema = z.object({
    recipeId: uuidSchema,
    reason: z.string().trim().min(3).max(2000),
});
export const abuseReportUpdateSchema = z.object({
    status: abuseReportStatusSchema,
    note: z.string().max(1000).optional(),
});
export const auditLogEntrySchema = z.object({
    id: uuidSchema,
    actorId: uuidSchema.nullable(),
    actorEmail: z.string().nullable(),
    action: z.string(),
    targetType: z.string().nullable(),
    targetId: z.string().nullable(),
    metadata: z.record(z.unknown()).nullable(),
    ip: z.string().nullable(),
    createdAt: isoDateTimeSchema,
});
export const auditLogQuerySchema = cursorQuerySchema.extend({
    actorId: uuidSchema.optional(),
    action: z.string().max(100).optional(),
    targetType: z.string().max(100).optional(),
});
export const auditLogPageSchema = cursorPageSchema(auditLogEntrySchema);
export const supportAccessRequestSchema = z.object({
    userId: uuidSchema,
    reason: z.string().trim().min(10).max(1000),
    ttlMinutes: z.number().int().min(5).max(240).default(60),
});
export const supportAccessGrantSchema = z.object({
    id: uuidSchema,
    userId: uuidSchema,
    adminId: uuidSchema,
    reason: z.string(),
    expiresAt: isoDateTimeSchema,
    createdAt: isoDateTimeSchema,
});
//# sourceMappingURL=admin.js.map